import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquareText, Plus, Eye, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["New Lead", "Interested", "Details Sent", "Call Back", "Webinar", "Payment", "General Follow-up", "Custom"];
const VARS = ["customer_name", "employee_name", "employee_phone", "company_name", "pdf_link", "video_link", "website_link", "lead_id", "product_name"];

export default function WhatsAppTemplates() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);   // {id?, name, body, category, active}
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/whatsapp-templates?include_inactive=true").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.name || !edit.body) return toast.error("Name and message are required");
    setBusy(true);
    try {
      if (edit.id) await api.patch(`/whatsapp-templates/${edit.id}`, edit);
      else await api.post("/whatsapp-templates", edit);
      toast.success("Template saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const doPreview = async () => {
    try { const { data } = await api.post("/whatsapp-templates/preview", { body: edit.body }); setPreview(data.preview); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (t) => { await api.patch(`/whatsapp-templates/${t.id}`, { active: !t.active }); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><MessageSquareText className="h-7 w-7 text-primary" /> WhatsApp Templates</h1>
          <p className="text-muted-foreground text-sm mt-1">Deep-link message templates telecallers send manually. No WhatsApp API — the employee sends the message.</p>
        </div>
        <Button onClick={() => { setEdit({ name: "", body: "", category: "General Follow-up", active: true }); setPreview(""); }} data-testid="new-template-button" className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1.5" />New Template</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {!items ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> :
          items.map((t) => (
            <div key={t.id} data-testid={`template-${t.id}`} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-[11px] text-muted-foreground">{t.category || "General"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={t.active} onCheckedChange={() => toggle(t)} data-testid={`template-active-${t.id}`} />
                  <Button size="sm" variant="ghost" onClick={() => { setEdit({ ...t }); setPreview(""); }} data-testid={`edit-template-${t.id}`} className="text-blue-400">Edit</Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-3 whitespace-pre-wrap">{t.body}</p>
            </div>
          ))}
        {items && items.length === 0 && <p className="text-muted-foreground text-sm">No templates yet.</p>}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} Template</DialogTitle>
            <DialogDescription className="text-muted-foreground">Use variables like {"{{customer_name}}"} — they fill in when a telecaller sends it.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-xs text-muted-foreground">Name</Label>
                <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="template-name" className="bg-slate-900/60 border-slate-700 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Category</Label>
                <select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="template-category" className="w-full mt-1 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><Label className="text-xs text-muted-foreground">Message</Label>
                <Textarea value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} rows={5} data-testid="template-body" className="bg-slate-900/60 border-slate-700 mt-1" />
                <p className="text-[11px] text-muted-foreground mt-1">{VARS.map((v) => `{{${v}}}`).join("  ")}</p></div>
              <button onClick={doPreview} data-testid="template-preview-button" className="text-xs text-primary flex items-center gap-1"><Eye className="h-3 w-3" /> Preview with sample data</button>
              {preview && <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm whitespace-pre-wrap" data-testid="template-preview">{preview}</div>}
              <div className="flex items-center gap-2"><Label className="text-xs text-muted-foreground">Active</Label><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="save-template-button" className="bg-primary text-primary-foreground">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save</>}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
